import React, { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

interface LazyRenderListProps {
  /** Total number of items that could be rendered. */
  itemCount: number;
  /** Renders a single item by its index. Only visible items are rendered. */
  renderItem: (index: number) => React.ReactNode;
  /** How many items to render initially and to append on each scroll batch. */
  batchSize?: number;
  /** Max height of the scrollable area before it starts scrolling. */
  maxHeight?: number | string;
  /** Distance (px) from the bottom at which the next batch is loaded. */
  threshold?: number;
  /** Style applied to the inner flex container that wraps the items. */
  innerStyle?: CSSProperties;
  /** Style applied to the scrollable container. */
  style?: CSSProperties;
}

const DEFAULT_BATCH_SIZE = 60;

/**
 * Renders a potentially huge list of items incrementally to avoid blocking the
 * main thread. Only `visibleCount` items are mounted at a time; more are
 * appended as the user scrolls towards the bottom.
 */
const LazyRenderList = ({
  itemCount,
  renderItem,
  batchSize = DEFAULT_BATCH_SIZE,
  maxHeight = 400,
  threshold = 200,
  innerStyle,
  style,
}: LazyRenderListProps) => {
  const { primaryColor } = useGomakeTheme();
  const safeItemCount = Number.isFinite(itemCount) && itemCount > 0 ? itemCount : 0;
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(safeItemCount, batchSize),
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisibleCount(Math.min(safeItemCount, batchSize));
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [safeItemCount, batchSize]);

  const loadMore = () => {
    setVisibleCount((current) =>
      current >= safeItemCount
        ? current
        : Math.min(safeItemCount, current + batchSize),
    );
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (reachedBottom) {
      loadMore();
    }
  };

  if (safeItemCount === 0) {
    return null;
  }

  const items: React.ReactNode[] = [];
  for (let index = 0; index < visibleCount; index += 1) {
    items.push(renderItem(index));
  }

  return (
    <Box sx={{ position: "relative", maxHeight, ...style }}>

      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          maxHeight,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: `${primaryColor(300)} #f0f0f0`,
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: primaryColor(300),
            borderRadius: 4,
            "&:hover": {
              backgroundColor: primaryColor(400),
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "15px",
            ...innerStyle,
          }}
        >
          {items}
        </Box>
      </Box>
    </Box>
  );
};

export { LazyRenderList };
